import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Users, ShieldCheck, MonitorPlay, Sparkles, Target, TrendingUp,
  LayoutDashboard, Inbox, Banknote, Mail, Radar, Building2, Package,
  ClipboardCheck, Brain, GraduationCap, LineChart, Share2, Wrench, LogOut, X,
} from 'lucide-react';
import { useRpc } from '../../lib/queries';
import type { InboxItem } from '../../lib/classes';
import { LogoMark, Wordmark } from '../ui/Logo';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}
interface NavGroup { label: string; items: NavItem[] }

/** The six pages Jamie runs the business from, then everything else under General. */
export const NAV_GROUPS: NavGroup[] = [
  { label: 'Business', items: [
    { to: '/users',      label: 'Users',      icon: Users },
    { to: '/compliance', label: 'Compliance', icon: ShieldCheck },
    { to: '/sessions',   label: 'Sessions',   icon: MonitorPlay },
    { to: '/vyo',        label: 'Vyo',        icon: Sparkles },
    { to: '/outcomes',   label: 'Outcomes',   icon: Target },
    { to: '/growth',     label: 'Growth',     icon: TrendingUp },
  ]},
  { label: 'General', items: [
    { to: '/',                       label: 'Overview',          icon: LayoutDashboard },
    { to: '/inbox',                  label: 'Inbox',             icon: Inbox },
    { to: '/revenue',                label: 'Revenue',           icon: Banknote },
    { to: '/email',                  label: 'Email',             icon: Mail },
    { to: '/alerts',                 label: 'Radar',             icon: Radar },
    { to: '/catalogue',              label: 'Firms and tracker', icon: Building2 },
    { to: '/packs',                  label: 'Packs',             icon: Package },
    { to: '/psychometrics',          label: 'Psychometrics',     icon: ClipboardCheck },
    { to: '/psychometric/authoring', label: 'Item authoring',    icon: Brain },
    { to: '/learn',                  label: 'Learn',             icon: GraduationCap },
    { to: '/events',                 label: 'Events',            icon: LineChart },
    { to: '/referrals',              label: 'Referrals',         icon: Share2 },
    { to: '/ops',                    label: 'Ops',               icon: Wrench },
  ]},
];

const NavButton: React.FC<{ item: NavItem; badge?: React.ReactNode; onNavigate?: () => void }> = ({ item, badge, onNavigate }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          'tap flex items-center gap-2.5 px-2.5 h-11 lg:h-8 rounded-md text-[15px] lg:text-[13.5px] transition-colors',
          isActive ? 'bg-stone-100 text-stone-900 font-semibold' : 'text-stone-600 font-medium hover:bg-stone-50 hover:text-stone-900',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-indigo-600' : 'text-stone-400'}`} />
          <span className="truncate flex-1">{item.label}</span>
          {badge}
        </>
      )}
    </NavLink>
  );
};

export const Sidebar: React.FC<{
  userEmail: string | null;
  onSignOut: () => void;
  /** The drawer passes these; the desktop sidebar has neither. */
  onNavigate?: () => void;
  onClose?: () => void;
  className?: string;
}> = ({ userEmail, onSignOut, onNavigate, onClose, className = '' }) => {
  const { data: inbox } = useRpc<InboxItem[]>('admin_inbox', {}, { refetchInterval: 120_000 });
  const urgent = (inbox ?? []).filter(i => i.severity !== 'info');
  const hasAct = urgent.some(i => i.severity === 'act');

  const inboxBadge = urgent.length > 0 ? (
    <span className={[
      'text-[11px] font-semibold rounded px-1.5 tabular-nums',
      hasAct ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-800',
    ].join(' ')}>
      {urgent.length}
    </span>
  ) : undefined;

  return (
    <aside className={`flex flex-col bg-white border-r border-stone-200 h-full ${className}`}>
      <div className="px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-4 flex items-center gap-2.5">
        <LogoMark className="w-6 h-6" />
        <Wordmark />
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close menu" className="ml-auto -mr-2 inline-flex items-center justify-center w-11 h-11 rounded-md text-stone-500 active:bg-stone-100">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 min-h-0 px-3 pb-3 overflow-y-auto overscroll-contain">
        {NAV_GROUPS.map(group => (
          <div key={group.label} className="mb-4">
            <div className="px-2.5 pb-1.5">
              <span className="text-[12px] font-medium text-stone-400">{group.label}</span>
            </div>
            <div className="space-y-px">
              {group.items.map(item => (
                <NavButton key={item.to} item={item} onNavigate={onNavigate} badge={item.to === '/inbox' ? inboxBadge : undefined} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-stone-200">
        <div className="px-2.5 pb-2 text-[12px] text-stone-500 truncate">{userEmail ?? 'Signed in'}</div>
        <button
          onClick={onSignOut}
          className="w-full flex items-center gap-2 px-2.5 h-11 lg:h-8 rounded-md text-[15px] lg:text-[13px] font-medium text-stone-600 hover:bg-stone-50 hover:text-stone-900 transition-colors"
        >
          <LogOut className="w-4 h-4 text-stone-400" /> Sign out
        </button>
      </div>
    </aside>
  );
};
